import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, FileQuestion, FileText, Search, X } from 'lucide-react';
import { courseService, questionService, searchService } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import {
  AppShell, Breadcrumbs, Button, EmptyState, Loader, PageHeader, SectionTitle, Segmented,
} from '../components/ui';
import './SearchResults.css';

/** Remove a marcação Markdown dos trechos (negrito, títulos, tabelas, links, código). */
function plainSnippet(text) {
  return String(text)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    .replace(/[*_`~>|]+/g, ' ')
    .replace(/^\s*[-+]\s+/gm, '')
    .replace(/-{3,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const TYPE_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'topics', label: 'Tópicos' },
  { value: 'lessons', label: 'Aulas' },
  { value: 'questions', label: 'Questões' },
];

function SearchResults() {
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const { currentCourse, selectCourse } = useCourse();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ topics: [], lessons: [], questions: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [topics, setTopics] = useState([]);
  const [questionCount, setQuestionCount] = useState(null);

  const buildPath = (path) => `/curso/${courseSlug}${path}`;

  useEffect(() => {
    if (courseSlug) selectCourse(courseSlug);
  }, [courseSlug, selectCourse]);

  // Tópicos (com aulas) alimentam os atalhos do estado vazio e a localização dos resultados
  useEffect(() => {
    if (!courseSlug) return;
    courseService.getTopicsByCourse(courseSlug, true).then(setTopics).catch(() => setTopics([]));
    questionService.countQuestions(courseSlug).then(setQuestionCount).catch(() => setQuestionCount(null));
  }, [courseSlug]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      setTypeFilter('all');
      performSearch(q);
    } else {
      setQuery('');
      setSearched(false);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setSearched(true);
    setSearchError(false);
    try {
      const data = await searchService.search(searchQuery, courseSlug);
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setResults({ topics: [], lessons: [], questions: [] });
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchParams({});
  };

  const sortedTopics = [...topics].sort((a, b) => (a.fase || 0) - (b.fase || 0));
  const topicById = new Map(topics.map(t => [t.id, t]));
  const lessonTotal = topics.reduce((acc, t) => acc + (t.lessons?.length || 0), 0);
  const phaseLabel = (fase) => (fase ? `Fase ${String(fase).padStart(2, '0')}` : '');

  const totalResults = (results.topics?.length || 0) + (results.lessons?.length || 0) + (results.questions?.length || 0);
  const lastQuery = searchParams.get('q') || query;

  const groups = [
    {
      key: 'topics',
      label: 'Tópicos',
      icon: BookOpen,
      tone: 'navy',
      items: results.topics || [],
      where: (item) => phaseLabel(topicById.get(item.id)?.fase),
      onOpen: (item) => navigate(buildPath(`/estudo/${item.id}`)),
    },
    {
      key: 'lessons',
      label: 'Aulas',
      icon: FileText,
      tone: 'navy',
      items: results.lessons || [],
      where: (item) => {
        const topic = topicById.get(item.topicId);
        if (!topic) return '';
        const index = (topic.lessons || []).findIndex(l => l.id === item.id);
        return index >= 0 ? `${topic.name} · aula ${index + 1}` : topic.name;
      },
      onOpen: (item) => navigate(buildPath(`/estudo/${item.topicId}/aula/${item.id}`)),
    },
    {
      key: 'questions',
      label: 'Questões',
      icon: FileQuestion,
      tone: 'lime',
      items: results.questions || [],
      where: () => '',
      onOpen: null,
    },
  ];
  const visibleGroups = groups.filter(g => g.items.length > 0 && (typeFilter === 'all' || typeFilter === g.key));
  const countOf = (key) => (key === 'all' ? totalResults : groups.find(g => g.key === key).items.length);

  const hasQuery = Boolean(query.trim());
  const kinds = [
    { key: 'topics', label: 'Tópicos', icon: BookOpen, tone: 'navy', hint: topics.length ? `${topics.length} no curso` : 'nomes e descrições' },
    { key: 'lessons', label: 'Aulas', icon: FileText, tone: 'navy', hint: lessonTotal ? `${lessonTotal} no curso` : 'títulos e conteúdo' },
    { key: 'questions', label: 'Questões', icon: FileQuestion, tone: 'lime', hint: questionCount ? `${questionCount} no banco` : 'enunciados' },
  ];

  return (
    <AppShell section="Buscar" courseName={currentCourse?.name}>
      <div className="ss-page ss-page--crumbs">
        <Breadcrumbs items={[{ label: 'Modo Estudo', onClick: () => navigate(buildPath('/estudo')) }, { label: 'Buscar' }]} />
        <PageHeader icon={Search} eyebrow={currentCourse?.name} title="Buscar" subtitle="Encontre tópicos, aulas e questões do curso." />

        <form onSubmit={handleSearch} className={`sr-form ${searched ? 'has-search' : ''}`} role="search" data-tour="buscar">
          <Search size={20} aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ex.: kubernetes, cache, arquitetura hexagonal..."
            aria-label="Termo de busca"
            autoFocus
          />
          {hasQuery && (
            <button type="button" className="sr-form__clear" onClick={clearSearch} aria-label="Limpar busca">
              <X size={14} aria-hidden="true" />
            </button>
          )}
          <span className="sr-form__kbd" aria-hidden="true">Enter ↵</span>
          <Button type="submit" variant={hasQuery ? 'accent' : 'primary'} disabled={!hasQuery}>Buscar</Button>
        </form>

        {loading && <Loader label="Buscando..." />}

        {searched && !loading && searchError && (
          <EmptyState
            file="busca.xml"
            title="A busca está indisponível no momento"
            description="O servidor não respondeu à busca. Tente de novo em instantes; se continuar, o serviço de busca pode não estar publicado."
            action={<Button variant="outline" onClick={() => performSearch(lastQuery)}>Tentar novamente</Button>}
          />
        )}

        {searched && !loading && !searchError && (
          totalResults === 0 ? (
            <EmptyState
              file="busca.xml"
              title={`Nenhum resultado para "${lastQuery}"`}
              description="Tente outro termo, uma palavra mais curta ou verifique a ortografia."
            />
          ) : (
            <>
              <div className="sr-toolbar">
                <Segmented
                  ariaLabel="Tipo de resultado"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={TYPE_FILTERS.map(f => ({ ...f, count: countOf(f.value) }))}
                />
                <p className="sr-count">
                  <strong>{totalResults} {totalResults === 1 ? 'resultado' : 'resultados'}</strong> para “{lastQuery}”
                </p>
              </div>

              {visibleGroups.length === 0 ? (
                <p className="sr-none">Nenhum resultado deste tipo.</p>
              ) : (
                <div className="sr-groups">
                  {visibleGroups.map(group => (
                    <section key={group.key} className="sr-group">
                      <SectionTitle icon={group.icon} aside={group.items.length}>{group.label}</SectionTitle>
                      <div className="sr-list">
                        {group.items.map(item => {
                          const Tag = group.onOpen ? 'button' : 'div';
                          const GroupIcon = group.icon;
                          const where = group.where(item);
                          const title = item.name || item.title;
                          // Questões costumam trazer o próprio enunciado como trecho: não repete
                          const snippet = item.snippet ? plainSnippet(item.snippet) : '';
                          const plainTitle = plainSnippet(title || '');
                          const showSnippet = snippet && !plainTitle.startsWith(snippet.replace(/^\.+/, '').slice(0, 40));
                          return (
                            <Tag
                              key={`${group.key}-${item.id}`}
                              type={group.onOpen ? 'button' : undefined}
                              className={`sr-item ${group.onOpen ? 'sr-item--link' : ''}`}
                              onClick={group.onOpen ? () => group.onOpen(item) : undefined}
                            >
                              <span className={`sr-item__icon is-${group.tone}`}>
                                <GroupIcon size={15} aria-hidden="true" />
                              </span>
                              <span className="sr-item__body">
                                <span className="sr-item__title">{title}</span>
                                {showSnippet && <span className="sr-item__snippet">{snippet}</span>}
                              </span>
                              {where && <span className="sr-item__where">{where}</span>}
                              {group.onOpen && <ChevronRight size={18} className="sr-item__go" aria-hidden="true" />}
                            </Tag>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </>
          )
        )}

        {!searched && !loading && (
          <div className="sr-empty">
            {sortedTopics.length > 0 && (
              <div className="sr-empty__block">
                <span className="sr-empty__title">Buscar por tópico</span>
                <div className="sr-chips">
                  {sortedTopics.map(topic => (
                    <button
                      key={topic.id}
                      type="button"
                      className="sr-chip"
                      onClick={() => setSearchParams({ q: topic.name })}
                    >
                      {topic.fase && <span className="sr-chip__phase">F{String(topic.fase).padStart(2, '0')}</span>}
                      {topic.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="sr-empty__block">
              <span className="sr-empty__title">O que a busca encontra</span>
              <div className="sr-kinds">
                {kinds.map(({ key, label, icon: Icon, tone, hint }) => (
                  <div key={key} className="sr-kind">
                    <span className={`sr-item__icon sr-kind__icon is-${tone}`}><Icon size={17} aria-hidden="true" /></span>
                    <span className="sr-kind__text">
                      <strong>{label}</strong>
                      <span>{hint}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

export default SearchResults;
